import { Injectable } from '@angular/core';
import {
  ChartConfiguration,
  ChartDataset,
  ElementOptionsByType,
  PluginOptionsByType,
  ScaleChartOptions,
  TooltipOptions,
} from 'chart.js';
import { DeepPartial } from 'node_modules/chart.js/dist/types/utils';

import { TrialInsightsChartModel } from '../../models/trial-insights-chart.model';

// TrialInsightsClinicalInvestigatorCost
export type TICICChartLabel = [string, number][];
// TrialInsightsClinicalInvestigatorCost
export type TICICChartType = 'bar';

@Injectable({
  providedIn: 'root',
})
export class TrialInsightsClinicalInvestigatorCostChartService
  implements TrialInsightsChartModel<TICICChartType, TICICChartLabel>
{
  elementOptions = (): DeepPartial<ElementOptionsByType<TICICChartType>> => {
    return {
      bar: {
        borderColor: '#bacad0',
      },
    };
  };

  tooltipOptions = (): DeepPartial<TooltipOptions<TICICChartType>> => {
    return {
      callbacks: {
        label: (tooltipItem) => {
          const label = tooltipItem.dataset.label;
          const value = tooltipItem.formattedValue;

          return `${label}: ${value}%`;
        },
      },
    };
  };

  pluginOptions = (): DeepPartial<PluginOptionsByType<TICICChartType>> => {
    return {
      legend: {
        display: false,
      },
      tooltip: this.tooltipOptions(),
    };
  };

  xAxesOptions = (): DeepPartial<ScaleChartOptions<TICICChartType>> => {
    return {
      scales: {
        x1: {
          beginAtZero: true,
          grid: {
            color: '#bacad0',
            display: false,
            lineWidth: 0.75,
            z: 1,
          },
          max: 100,
          stacked: true,
          ticks: {
            display: false,
            stepSize: 10,
          },
          title: {
            display: false,
          },
        },
      },
    };
  };

  yAxesOptions = (): DeepPartial<ScaleChartOptions<TICICChartType>> => {
    return {
      scales: {
        y: {
          display: false,
          stacked: true,
        },
      },
    };
  };

  chartOptions = (): ChartConfiguration<TICICChartType>['options'] => {
    return {
      elements: this.elementOptions(),
      indexAxis: 'y',
      maintainAspectRatio: false,
      plugins: this.pluginOptions(),
      responsive: true,
      scales: {
        ...this.xAxesOptions().scales,
        ...this.yAxesOptions().scales,
      },
    };
  };

  labelOptions = () => {
    return ['Investigator Costs'];
  };

  createChart = (datasets?: ChartDataset<TICICChartType>[]): ChartConfiguration<TICICChartType> => {
    return {
      data: {
        datasets: datasets ? datasets : [],
        labels: this.labelOptions(),
      },
      options: this.chartOptions(),
      type: 'bar',
    };
  };

  createDatasets = (labels?: TICICChartLabel): ChartDataset<TICICChartType>[] => {
    const datasets: ChartDataset<TICICChartType>[] = [];

    if (!labels?.length) {
      return datasets;
    }

    const colors = ['#226263', '#437f7f', '#6e9797', '#4e6d79', '#8b9fa7'];
    const hoverColors = ['#207d7e', '#449494', '#75b7b7', '#588496', '#90aeba'];

    labels.forEach(([label, percent], index) => {
      datasets.push({
        backgroundColor: [colors[index]],
        barThickness: 85,
        data: [percent],
        hoverBackgroundColor: [hoverColors[index]],
        label: label,
        xAxisID: 'x1',
      });
    });

    return datasets;
  };
}
